import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useEvents } from "@/hooks/useEvents";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, Clock, MapPin, Plus } from "lucide-react";
import { format, isToday, isTomorrow, isThisWeek } from "date-fns";
import { useNavigate } from "react-router-dom";

export const UpcomingEventsWidget = () => {
  const navigate = useNavigate();
  
  // Get events for the next 7 days
  const today = new Date();
  const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  
  const { data: upcomingEvents, isLoading } = useEvents(today, nextWeek);

  const getEventBadgeVariant = (eventDate: Date) => {
    if (isToday(eventDate)) return "destructive";
    if (isTomorrow(eventDate)) return "secondary";
    if (isThisWeek(eventDate)) return "outline";
    return "outline";
  };

  const getEventBadgeText = (eventDate: Date) => {
    if (isToday(eventDate)) return "Today";
    if (isTomorrow(eventDate)) return "Tomorrow";
    if (isThisWeek(eventDate)) return format(eventDate, "EEEE");
    return format(eventDate, "MMM d");
  };

  const getEventTime = (startTime: string, endTime?: string) => {
    const start = format(new Date(startTime), "h:mm a");
    if (endTime) {
      const end = format(new Date(endTime), "h:mm a");
      return `${start} - ${end}`;
    }
    return start;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-medium">Upcoming Events</CardTitle>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-start space-x-3">
                <Skeleton className="h-4 w-4 mt-1" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <Skeleton className="h-5 w-16" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const sortedEvents = upcomingEvents?.sort((a, b) => 
    new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  ) || [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base font-medium">Upcoming Events</CardTitle>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/calendar')}
            className="h-8 px-2 text-xs"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Event
          </Button>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        {!sortedEvents || sortedEvents.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No upcoming events</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/calendar')}
              className="mt-2"
            >
              <Plus className="h-3 w-3 mr-1" />
              Schedule Event
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedEvents.slice(0, 5).map((event) => {
              const eventDate = new Date(event.start_time);
              return (
                <div key={event.id} className="flex items-start space-x-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-shrink-0 mt-1">
                    <div className={`w-2 h-2 rounded-full ${
                      isToday(eventDate) ? 'bg-destructive' : 
                      isTomorrow(eventDate) ? 'bg-primary' : 'bg-muted-foreground'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{event.title}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="flex items-center text-xs text-muted-foreground">
                        <Clock className="h-3 w-3 mr-1" />
                        {getEventTime(event.start_time, event.end_time)}
                      </div>
                      {event.location && (
                        <div className="flex items-center text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3 mr-1" />
                          <span className="truncate max-w-20">{event.location}</span>
                        </div>
                      )}
                    </div>
                    {event.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                        {event.description}
                      </p>
                    )}
                  </div>
                  <Badge variant={getEventBadgeVariant(eventDate)} className="text-xs">
                    {getEventBadgeText(eventDate)}
                  </Badge>
                </div>
              );
            })}
            {sortedEvents.length > 5 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/calendar')}
                className="w-full mt-2 text-xs"
              >
                View All Events ({sortedEvents.length})
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};